import { useEffect, useState } from 'react';
import {
  Box, Button, Drawer, InputAdornment, List, ListItemButton, ListItemIcon, ListItemText,
  Paper, Tab, Tabs, TextField, Theme, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MenuIcon from '@mui/icons-material/Menu';
import { useDebounce } from '../../../shared/hooks';
import { ContaBancariaService, IListagemConta, TTipoContaFinanceira } from '../../../shared/services/api/cadastros/ContaBancaria';

interface ISidebarContasConciliacaoProps {
  contaSelecionadaId: string;
  aoSelecionarConta: (idconta: string, descricao: string, tipo: TTipoContaFinanceira) => void;
}

type TAbaConta = 'financeiras' | 'recebiveis';

const ICONE_POR_TIPO: Record<TTipoContaFinanceira, React.ReactElement> = {
  banco: <AccountBalanceIcon fontSize="small" />,
  caixa: <PointOfSaleIcon fontSize="small" />,
  cofre: <LockIcon fontSize="small" />,
  recebiveis: <CreditCardIcon fontSize="small" />,
};

const LARGURA_SIDEBAR = 280;

const ListaDeContas: React.FC<{
  contas: IListagemConta[];
  isLoading: boolean;
  contaSelecionadaId: string;
  aoClicarConta: (conta: IListagemConta) => void;
}> = ({ contas, isLoading, contaSelecionadaId, aoClicarConta }) => {
  if (isLoading) {
    return <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>Carregando...</Typography>;
  }
  if (contas.length === 0) {
    return <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>Nenhuma conta encontrada.</Typography>;
  }
  return (
    <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
      {contas.map((conta) => (
        <ListItemButton
          key={conta.idconta}
          selected={conta.idconta === contaSelecionadaId}
          onClick={() => aoClicarConta(conta)}
          sx={{ py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {conta.tipo ? ICONE_POR_TIPO[conta.tipo] : <AccountBalanceIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={conta.titular}
            primaryTypographyProps={{ variant: 'body2', fontWeight: conta.idconta === contaSelecionadaId ? 700 : 400, noWrap: true }}
          />
        </ListItemButton>
      ))}
    </List>
  );
};

// Painel único (Tabs Financeiras/Recebíveis + busca + lista), reaproveitado
// tanto no modo fixo (desktop) quanto dentro do Drawer (mobile) — só o
// container ao redor muda.
const PainelDeContas: React.FC<{
  abaAtiva: TAbaConta;
  aoMudarAba: (aba: TAbaConta) => void;
  busca: string;
  aoMudarBusca: (busca: string) => void;
  financeiras: IListagemConta[];
  recebiveis: IListagemConta[];
  isLoading: boolean;
  contaSelecionadaId: string;
  aoClicarConta: (conta: IListagemConta) => void;
}> = ({ abaAtiva, aoMudarAba, busca, aoMudarBusca, financeiras, recebiveis, isLoading, contaSelecionadaId, aoClicarConta }) => (
  <Box display="flex" flexDirection="column" height="100%">
    <Tabs
      value={abaAtiva}
      onChange={(_, novaAba) => aoMudarAba(novaAba)}
      variant="fullWidth"
      sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem' } }}
    >
      <Tab label="Financeiras" value="financeiras" />
      <Tab label="Recebíveis" value="recebiveis" />
    </Tabs>
    <Box p={1}>
      <TextField
        size="small"
        fullWidth
        placeholder="Buscar conta..."
        value={busca}
        onChange={(e) => aoMudarBusca(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />
    </Box>
    <ListaDeContas
      contas={abaAtiva === 'financeiras' ? financeiras : recebiveis}
      isLoading={isLoading}
      contaSelecionadaId={contaSelecionadaId}
      aoClicarConta={aoClicarConta}
    />
  </Box>
);

// Sidebar de seleção de conta pra tela de Conciliação — separa contas
// "financeiras" (banco/caixa/cofre) de "recebíveis" (cartão/PIX), busca
// própria via ContaBancariaService.getAllList com o filtro `tipo` (ver
// backend controller/contas/GetAll.ts). Painel fixo a partir do breakpoint
// md; abaixo disso vira um botão que abre um Drawer local — não usa o
// DrawerContext do menu principal do app.
export const SidebarContasConciliacao: React.FC<ISidebarContasConciliacaoProps> = ({
  contaSelecionadaId,
  aoSelecionarConta,
}) => {
  const theme = useTheme();
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { debounce } = useDebounce();

  const [abaAtiva, setAbaAtiva] = useState<TAbaConta>('financeiras');
  const [busca, setBusca] = useState('');
  const [financeiras, setFinanceiras] = useState<IListagemConta[]>([]);
  const [recebiveis, setRecebiveis] = useState<IListagemConta[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [nomeContaSelecionada, setNomeContaSelecionada] = useState('');

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      Promise.all([
        ContaBancariaService.getAllList(busca, '', 'banco,caixa,cofre', 50),
        ContaBancariaService.getAllList(busca, '', 'recebiveis', 50),
      ]).then(([resultadoFinanceiras, resultadoRecebiveis]) => {
        setIsLoading(false);
        if (!(resultadoFinanceiras instanceof Error)) setFinanceiras(resultadoFinanceiras.data);
        if (!(resultadoRecebiveis instanceof Error)) setRecebiveis(resultadoRecebiveis.data);
      });
    });
  }, [busca]);

  const aoClicarConta = (conta: IListagemConta) => {
    setNomeContaSelecionada(conta.titular);
    aoSelecionarConta(conta.idconta, conta.titular, conta.tipo || 'banco');
    if (mdDown) setDrawerAberto(false);
  };

  const conteudoPainel = (
    <PainelDeContas
      abaAtiva={abaAtiva}
      aoMudarAba={setAbaAtiva}
      busca={busca}
      aoMudarBusca={setBusca}
      financeiras={financeiras}
      recebiveis={recebiveis}
      isLoading={isLoading}
      contaSelecionadaId={contaSelecionadaId}
      aoClicarConta={aoClicarConta}
    />
  );

  if (mdDown) {
    return (
      <>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<MenuIcon />}
          onClick={() => setDrawerAberto(true)}
          sx={{ mb: 1 }}
        >
          {nomeContaSelecionada || 'Selecionar conta'}
        </Button>
        <Drawer anchor="left" open={drawerAberto} onClose={() => setDrawerAberto(false)}>
          <Box width={LARGURA_SIDEBAR} height="100%">
            {conteudoPainel}
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        width: LARGURA_SIDEBAR,
        height: 'calc(100vh - 260px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.torq.surface.card,
        borderColor: theme.torq.border.default,
        borderRadius: `${theme.shape.borderRadius}px`,
      }}
    >
      {conteudoPainel}
    </Paper>
  );
};
